import type { AnalyticsGrain, AnalyticsSecurityDto } from '@pams/shared';
import { FigureList, RankedList } from '@/components/DataFigures';
import { formatCount, istDateTime, seriesPeak, seriesPointLabel, type SeriesPoint } from '@/lib/analyticsReport';
import { USAGE_ANALYTICS_COPY } from '../copy';
import { SubBlock } from './AnalyticsSection';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE SECURITY SIGNALS. [ANALYTICS §5]
 *
 *  ⚠️ EVERY FIGURE SAYS WHERE IT COMES FROM. Refusals by kind, the address and network clusters,
 *  reuse revocations and the lockouts set in the window were first COUNTED in this release, so a
 *  window reaching back before it shows zeros that mean "not recorded" rather than "did not
 *  happen" — and the one existing record read here, the lockouts active now, says it is one.
 *
 *  ⚠️ AN ADDRESS IS A NETWORK. A police station's whole shift arrives from one NAT address, so
 *  the busiest address is ordinarily a station, and "routine" refusals (an expired sign-in) are
 *  marked and kept out of the clusters by the server rather than ranked beside real ones.
 *
 *  ⚠️ PER-ADDRESS DETAIL HAS A RETENTION RULE, AND ITS EDGE IS THE WIRE'S DATE — never a number of
 *  days written here. The retention is a server setting the page does not receive, and the date
 *  is read from the rows actually loaded, so it stays true when the setting changes.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const SecuritySection = ({ data, grain }: { data: AnalyticsSecurityDto; grain: AnalyticsGrain }) => {
  const copy = USAGE_ANALYTICS_COPY.security;
  const reusePoints: SeriesPoint[] = data.reuseRevocations.series.map((point) => ({
    at: point.at,
    values: [point.count],
  }));
  const reusePeak = seriesPeak(reusePoints);
  const otherIps = copy.otherIps(data.otherIpRequests);

  return (
    <div className="flex flex-col gap-6">
      {data.ipDetailAvailableFrom === null ? null : (
        <p className="text-sm text-muted-foreground" data-testid="ip-detail-removed">
          {copy.ipDetailRemoved(istDateTime(data.ipDetailAvailableFrom))}
        </p>
      )}

      <div className="grid gap-6 @min-[880px]:grid-cols-2">
        <SubBlock title={copy.topIpsHeading} caption={copy.topIpsCaption(data.lookupEnabled)}>
          {data.topIps.length === 0 ? (
            <p className="text-sm text-muted-foreground">{copy.noIps}</p>
          ) : (
            <RankedList
              rows={data.topIps.map((entry) => ({
                key: entry.ip,
                label: entry.ip,
                value: entry.requests,
                caption: copy.topIpCaption(data.lookupEnabled, entry.place, entry.rateLimited),
              }))}
            />
          )}
          {otherIps ? <p className="text-xs text-muted-foreground">{otherIps}</p> : null}
        </SubBlock>

        <SubBlock title={copy.failuresHeading} caption={copy.failuresCaption}>
          {data.authFailuresByKind.length === 0 ? (
            <p className="text-sm text-muted-foreground">{copy.noFailures}</p>
          ) : (
            <FigureList
              rows={data.authFailuresByKind.map((entry) => ({
                key: entry.kind,
                label: entry.routine ? `${copy.kind[entry.kind]} ${copy.routine}` : copy.kind[entry.kind],
                value: formatCount(entry.count),
              }))}
            />
          )}
        </SubBlock>

        <SubBlock title={copy.clustersHeading} caption={copy.clustersCaption}>
          {data.ipClusters.length === 0 ? (
            <p className="text-sm text-muted-foreground">{copy.noClusters}</p>
          ) : (
            <RankedList
              rows={data.ipClusters.map((cluster) => {
                const top = cluster.kinds[0];
                return {
                  key: cluster.ip,
                  label: cluster.ip,
                  value: cluster.failures,
                  caption: copy.clusterRow(
                    top === undefined ? null : copy.kind[top.kind],
                    Math.max(0, cluster.kinds.length - 1),
                  ),
                };
              })}
            />
          )}
        </SubBlock>

        <SubBlock title={copy.subnetsHeading} caption={copy.subnetsCaption}>
          {data.subnetClusters.length === 0 ? (
            <p className="text-sm text-muted-foreground">{copy.noSubnets}</p>
          ) : (
            <RankedList
              rows={data.subnetClusters.map((cluster) => ({
                key: cluster.subnet,
                label: cluster.subnet,
                value: cluster.failures,
                caption: copy.subnetRow(cluster.ips),
              }))}
            />
          )}
        </SubBlock>

        <SubBlock title={copy.reuseHeading} caption={copy.reuseCaption}>
          <p className="text-sm text-foreground" data-testid="reuse-summary">
            {copy.reuseSummary(
              data.reuseRevocations.total,
              reusePeak === null ? null : { label: seriesPointLabel(reusePeak.at, grain), total: reusePeak.total },
              grain,
            )}
          </p>
        </SubBlock>

        <SubBlock title={copy.bansHeading}>
          <FigureList
            rows={[
              {
                key: 'set',
                label: (
                  <span className="flex flex-col">
                    <span>{copy.bansSet}</span>
                    <span className="text-xs">{copy.bansSetCaption}</span>
                  </span>
                ),
                value: formatCount(data.otpBans.setInWindow),
              },
              {
                key: 'active',
                label: (
                  <span className="flex flex-col">
                    <span>{copy.bansActive}</span>
                    <span className="text-xs">{copy.bansActiveCaption}</span>
                  </span>
                ),
                value:
                  data.otpBans.activeNow === null ? copy.bansActiveUnavailable : formatCount(data.otpBans.activeNow),
              },
            ]}
          />
        </SubBlock>
      </div>
    </div>
  );
};
